import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import fpPromise from '@fingerprintjs/fingerprintjs';
import { BACKEND_URL } from '../utils/backendUrl';
import * as authUtils from '../utils/auth';
import { sanitizeEventInputForApi } from '../utils/planningIntent';
import {
  getInFlightThemeJob,
  saveInFlightThemeJob,
  clearInFlightThemeJob,
  getPendingThemeJobStart,
  savePendingThemeJobStart,
  clearPendingThemeJobStart
} from '../utils/asyncJobs';

const LoadingScreen = ({ eventData, currentPlanId, onThemesGenerated, onError }) => {
  const stepDurationMs = 1000;
  const avgTimeSeconds = 28;
  const maxProgressBeforeComplete = 94;

  const focusSel = eventData?.focus_selection || [];
  const isThemeBased = !focusSel.length || focusSel.includes('everything') || focusSel.includes('theme_ideas');

  const stepDefs = useMemo(
    () => isThemeBased
      ? [
          { text: 'Understanding your event' },
          { text: 'Designing distinct theme directions' },
          { text: 'Checking fit, budget, and realism' },
          { text: 'Preparing polished previews' }
        ]
      : [
          { text: 'Understanding your event' },
          { text: 'Building your plan structure' },
          { text: 'Checking fit, budget, and realism' },
          { text: 'Packaging your plan' }
        ],
    [isThemeBased]
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [doneSteps, setDoneSteps] = useState(() => stepDefs.map(() => false));
  const [progress, setProgress] = useState(0);
  const [hasError, setHasError] = useState(!eventData);
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    if (!eventData) { setHasError(true); return; }

    const cleanEventData = sanitizeEventInputForApi(eventData);

    const requestEventData = {
      ...cleanEventData,
      additional_details: eventData.__aiAdditionalDetails || cleanEventData.additional_details || ''
    };
    const callbackEventData = { ...cleanEventData };
    delete callbackEventData.__aiAdditionalDetails;

    const eventDataKey = JSON.stringify({
      event_type: eventData.event_type,
      budget: eventData.budget,
      guest_count: eventData.guest_count,
      zip_code: eventData.zip_code,
      age_range: eventData.age_range,
      venue_type: eventData.venue_type,
      meals: eventData.meals,
      theme_input: eventData.theme_input,
      theme_count: eventData.theme_count,
	      theme_styles: eventData.theme_styles,
	      theme_modifiers: eventData.theme_modifiers,
	      additional_details: eventData.additional_details,
	      ai_additional_details: eventData.__aiAdditionalDetails || '',
	      append_themes: Boolean(eventData.__appendThemes),
	      similar_to_theme_key: eventData.__similarToThemeKey || '',
	      fresh_set_request_id: eventData.__freshSetRequestId || ''
	    });
    setHasError(false);
    let isActive = true;
    let hasCalledCallback = false;
    const startTime = Date.now();
    const minTotalTimeMs = 2000;
    const tickIds = [];

    // Stepper UI ticks
    stepDefs.slice(1).forEach((_, idx) => {
      const stepIndex = idx + 1;
      tickIds.push(setTimeout(() => {
        if (!isActive) return;
        setDoneSteps(prev => { const next = [...prev]; next[stepIndex - 1] = true; return next; });
        setCurrentStep(stepIndex);
        setProgress(prev => Math.max(prev, Math.round((stepIndex / stepDefs.length) * 80)));
      }, stepIndex * stepDurationMs));
    });

    // Time-based progress bar
    const updateProgress = () => {
      if (!isActive) return;
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setElapsedTime(elapsed);
      let calculated;
      if (elapsed <= avgTimeSeconds) {
        const easedRatio = 1 - Math.pow(1 - (elapsed / avgTimeSeconds), 3);
        calculated = easedRatio * 90;
      } else {
        // Continuous subtle progression past average time so it never looks frozen
        const extraSecs = elapsed - avgTimeSeconds;
        calculated = 90 + Math.min(5, (extraSecs / 15) * 5);
      }
      setProgress(prev => Math.max(prev, Math.min(calculated, maxProgressBeforeComplete)));
    };
    const timeInterval = setInterval(updateProgress, 1000);
    updateProgress();

    const finalize = (data) => {
      if (!isActive || hasCalledCallback) return;
      hasCalledCallback = true;
      setDoneSteps(stepDefs.map(() => true));
      setProgress(100);
      setTimeout(() => {
        if (!isActive) return;
        try {
          if (data?.success && data?.themes && Array.isArray(data.themes) && data.themes.length > 0) {
            const requested = Number(eventData?.theme_count);
            const expected = [2, 4].includes(requested) ? requested : (authUtils.isAuthenticated() ? 4 : 2);
            if (data.themes.length < expected) {
              console.warn(`Requested ${expected} themes but received ${data.themes.length}.`);
            }
            if (onThemesGenerated) onThemesGenerated(data.themes, data.user_input_provided || false, callbackEventData);
          } else {
            console.error('Invalid theme data received:', data);
            if (onError) onError(); else alert('Failed to generate themes. Please try again.');
          }
        } catch (err) {
          console.error('Error in onThemesGenerated callback:', err);
          if (onError) onError(); else alert('An error occurred. Please try again.');
        }
      }, 700);
    };

    const generateThemes = async () => {
      let responseData = null;
      let apiError = null;

      try {
        if (!BACKEND_URL || BACKEND_URL === 'http://localhost:8001') {
          const isProd = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
          if (isProd) throw new Error('Backend URL not configured. Please set REACT_APP_BACKEND_URL in Vercel settings.');
        }

        let visitorId = 'unknown';
        try {
          const fp = await fpPromise.load();
          const result = await fp.get();
          visitorId = result.visitorId;
        } catch (fpErr) {
          console.error('Failed to get fingerprint:', fpErr);
        }

        const freshAuthHeader = authUtils.getFreshAuthHeader
          ? await authUtils.getFreshAuthHeader().catch(() => authUtils.getAuthHeader())
          : authUtils.getAuthHeader();

        let headers = {
          'Content-Type': 'application/json',
          'X-Device-Fingerprint': visitorId,
          ...freshAuthHeader
        };

        const refreshHeaders = async () => {
          const nextAuthHeader = authUtils.getFreshAuthHeader
            ? await authUtils.getFreshAuthHeader().catch(() => authUtils.getAuthHeader())
            : authUtils.getAuthHeader();
          headers = {
            'Content-Type': 'application/json',
            'X-Device-Fingerprint': visitorId,
            ...nextAuthHeader
          };
          return headers;
        };

        const axiosWithAuthRetry = async (requestFn) => {
          try {
            return await requestFn(headers);
          } catch (error) {
            if (error?.response?.status === 401 && authUtils.getRefreshToken()) {
              const nextHeaders = await refreshHeaders();
              return requestFn(nextHeaders);
            }
            throw error;
          }
        };

        const waitForStartedJob = async () => {
          for (let attempt = 0; attempt < 12; attempt += 1) {
            await new Promise((resolve) => setTimeout(resolve, 250));
            const startedJob = getInFlightThemeJob();
            if (startedJob?.jobId && startedJob.eventDataKey === eventDataKey) {
              return startedJob.jobId;
            }
          }
          return null;
        };

        const existingJob = getInFlightThemeJob();
        let jobId = existingJob?.jobId;

        if (!jobId || existingJob?.eventDataKey !== eventDataKey) {
          const pendingStart = getPendingThemeJobStart();
          if (pendingStart?.eventDataKey === eventDataKey) {
            jobId = await waitForStartedJob();
          }

          if (!jobId) {
            savePendingThemeJobStart({ eventDataKey });
            try {
              const startResponse = await axiosWithAuthRetry((authHeaders) => axios.post(
                `${BACKEND_URL}/api/jobs/theme-generation/start`,
                requestEventData,
                { timeout: 60000, headers: authHeaders }
              ));
              jobId = startResponse.data?.job_id;
              if (!jobId) {
                throw new Error('Theme generation job could not be started.');
              }
              saveInFlightThemeJob({
                jobId,
                eventDataKey,
                eventData: callbackEventData,
                currentPlanId: currentPlanId || null,
                createdAt: Date.now()
              });
            } finally {
              clearPendingThemeJobStart(eventDataKey);
            }
          }
        }

        while (isActive) {
          const pollResponse = await axiosWithAuthRetry((authHeaders) => axios.get(`${BACKEND_URL}/api/jobs/${jobId}`, {
            timeout: 60000,
            headers: authHeaders
          }));
          const job = pollResponse.data;
          if (job?.status === 'completed') {
            responseData = job.result;
            break;
          }
          if (job?.status === 'failed') {
            clearInFlightThemeJob();
            const detail = job?.error?.detail || 'Theme generation failed.';
            const statusCode = job?.error?.status_code || 500;
            const error = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
            error.response = { status: statusCode, data: { detail } };
            throw error;
          }
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      } catch (error) {
        if (error?.response?.status === 422) {
          console.error('[CMP_422_DEBUG] theme-generation payload', requestEventData);
          console.error('[CMP_422_DEBUG] theme-generation response', error?.response?.data);
          console.error('[CMP_422_DEBUG] Endpoint URL:', `${BACKEND_URL}/api/jobs/theme-generation/start`);
          console.error('[CMP_422_DEBUG] focus_selection:', eventData?.focus_selection);
          console.error('[CMP_422_DEBUG] event_type:', eventData?.event_type);
          console.error('[CMP_422_DEBUG] current planning intent:', eventData?.focus_selection);
        }
        if (error.code !== 'ERR_CANCELED' && error.name !== 'AbortError') {
          console.error('Theme generation error:', error.message);
        }
        if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
          apiError = new Error(`Network Error: Cannot connect to backend at ${BACKEND_URL}.`);
        } else if (error.code === 'ECONNREFUSED') {
          apiError = new Error(`Connection Refused: Backend at ${BACKEND_URL} is not accessible.`);
        } else {
          apiError = error;
        }
      }

      if (!isActive) return; // Cleaned-up mount — the surviving mount handles this

      if (apiError) {
        const detail = apiError.response?.data?.detail;
        const errorMessage = typeof detail === 'string'
          ? detail
          : detail?.message || apiError.message || 'Unknown error';
        
        // Handle rate limit/demo limit specifically
        if (apiError.response?.status === 429 || errorMessage.includes('limit reached')) {
          if (onError) onError(apiError); else alert(errorMessage);
          return;
        }

        if (apiError.response?.status === 403 && detail?.code === 'payment_required') {
          alert(errorMessage);
          if (onError) { window.__restoreStep = 'last'; onError(apiError); }
          return;
        }

        const userMessage = errorMessage.includes('Network Error') || errorMessage.includes('Cannot connect')
          ? `Network Error: Cannot connect to backend server.\n\nPlease check:\n1. REACT_APP_BACKEND_URL is set in Vercel\n2. Backend server is running\n3. Browser console (F12) for details`
          : `Failed to generate themes: ${errorMessage}`;
        alert(userMessage);
        if (onError) { window.__restoreStep = 'last'; onError(); }
        return;
      }

      const elapsed = Date.now() - startTime;
      setTimeout(() => finalize(responseData), Math.max(0, Math.min(500, minTotalTimeMs - elapsed)));
    };

    generateThemes();

    return () => {
      isActive = false;
      // so the surviving mount (or already-resolved value) can hand back the data.
      tickIds.forEach(id => clearTimeout(id));
      clearInterval(timeInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventData, onThemesGenerated, stepDefs]);


  // Show error state if eventData is missing
  if (hasError || !eventData) {
    return (
      <div className="max-w-lg mx-auto mt-20 animate-fade-in">
        <div className="ds-card p-12 text-center">
          <h2 className="text-xl font-bold mb-4" style={{ color: '#DC2626' }}>Error</h2>
          <p className="mb-4 ds-text-muted">Missing event data. Please try again.</p>
          {onError && (
            <button onClick={onError} className="ds-btn-primary px-6 py-2 text-sm">Go Back</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto pt-4 sm:pt-16 px-4 animate-fade-in">
      <div className="ds-card p-6 sm:p-12 text-center">
        <h2 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6" style={{ color: 'var(--brand-charcoal)', letterSpacing: '-0.02em' }}>{isThemeBased ? 'Creating your best-fit themes...' : 'Creating your custom plan...'}</h2>

        {/* Violet spinner */}
        <div className="flex justify-center mb-6">
          <div className="relative w-10 h-10">
            <div className="absolute inset-0 rounded-full border-4" style={{ borderColor: 'var(--brand-border)' }}></div>
            <div className="absolute inset-0 rounded-full border-4 border-transparent animate-spin"
              style={{ borderTopColor: 'var(--brand-violet)', borderRightColor: 'var(--brand-violet)' }}></div>
            <div className="absolute inset-2 rounded-full border-2 border-transparent animate-spin"
              style={{ borderTopColor: 'rgba(124,58,237,0.5)', animationDuration: '1.6s' }}></div>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="space-y-2.5 mb-6">
          {stepDefs.map((step, index) => {
            const done = doneSteps[index];
            const active = index === currentStep;
            return (
              <div
                key={index}
                className={`flex items-center justify-center text-xs sm:text-sm transition-all duration-300 ${
                  done ? 'font-medium' : active ? 'font-semibold' : ''
                }`}
                style={{ color: done ? '#059669' : active ? 'var(--brand-charcoal)' : 'var(--brand-muted)' }}
              >
                <span className="mr-2 text-base">
                  {done ? (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ display: 'inline' }}>
                      <circle cx="8" cy="8" r="8" fill="#059669" opacity="0.15" />
                      <path d="M5 8l2 2 4-4" stroke="#059669" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  ) : active ? (
                    <span style={{ color: 'var(--brand-violet)' }}>◉</span>
                  ) : '○'}
                </span>
                <span>{step.text}</span>
              </div>
            );
          })}
        </div>

        {/* Violet progress bar */}
        <div className="ds-progress-track h-1.5 w-full">
          <div className="ds-progress-fill h-full" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-1.5 text-xs ds-text-muted">{Math.round(progress)}%</p>
        
        <div className="mt-3 flex flex-col items-center gap-1 text-center">
          <p className="text-xs sm:text-sm font-medium text-stone-600 transition-all">
            {elapsedTime < 14
              ? 'Drafting customized themes and creative concepts...'
              : elapsedTime < 26
              ? 'Selecting tailored color palettes, decor, and styling...'
              : 'Polishing your theme recommendations and details...'}
          </p>
          <p className="text-[11px] text-stone-400">
            Elapsed: {elapsedTime}s · Usually takes 20–30 seconds
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;
